import { validateTranslationFlags } from '@src/components/sidepanel/compositionNormalizer/validateTranslationFlags';
import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { LogManager } from '@src/utility/Logger';

import { traverseCompositionElementPath } from './compositionElementPath';
import { normalizePhrase } from './normalizePhrase';

import type { TranslatableTarget } from './compositionTypes';
import type { ControlConfiguration } from '@src/components/sidepanel/CompositionSettings/types';

const logger = LogManager.getLogger('translation:extractTranslatableElements');

/**
 * Per-target shape emitted by the walk before dedupe. Identical to the wire
 * shape; deduping by normalized sourceText happens at the end.
 */
type ExtractWarningKind =
  | 'missing_meta'
  | 'multi_path_translatable'
  | 'unresolved_path'
  | 'non_string_value'
  | 'empty_value';

export interface ExtractWarning {
  kind: ExtractWarningKind;
  compositionElementId: string;
  controlId?: string;
  controlLabel?: string;
  details?: Record<string, unknown>;
}

export interface ExtractResult {
  /**
   * Deduped translatable targets ready for the backend request body. One
   * entry per unique (compositionElementId, sourceElement.id, property)
   * placement slot. The backend dedups again by normalized sourceText for
   * the LLM call; sending one target per placement preserves the full
   * references[] footprint the backend writes to phrase entries.
   */
  compositionTargets: TranslatableTarget[];
  /** Count of targets dropped because a preset translation already covers them. */
  presetSkipped: number;
  /** Count of unique normalized source texts in the final compositionTargets. */
  uniqueSourceTextCount: number;
  /** Non-fatal validation issues encountered during the walk. */
  warnings: ExtractWarning[];
}

interface ExtractParams {
  /**
   * Top-level `videoSource.elements` array. Typically read off
   * `VideoSourceStore.source.elements`. The extractor walks this tree
   * defensively — missing fields and unexpected shapes are skipped with a
   * warning rather than throwing.
   */
  videoSourceElements:
    | ReadonlyArray<Record<string, unknown>>
    | null
    | undefined;
  /**
   * `currentVideo.extraElementData` — bucket of per-element metadata. The
   * extractor reads `compositionControls` (parsed ControlConfiguration[])
   * and `templateId` from each composition placement's bucket.
   */
  extraElementData: Record<string, unknown> | null | undefined;
  /**
   * BCP 47 target language. Used to short-circuit targets whose template
   * preset (`control.translations[targetLanguage]`) already covers the
   * value — those never reach the backend.
   */
  targetLanguage: string;
}

/**
 * Walk the timeline and produce the `compositionTargets[]` payload PLAT-9964's
 * `/api/aiprompts/translations-generate` accepts.
 *
 * Plugin-side responsibilities (per PLAT-9870 epic plan):
 *
 * 1. Resolve every translatable control on every composition placement to its
 *    current source-element value via `elementPaths`.
 * 2. Drop targets covered by a template preset for the target language
 *    (`control.translations[lang]` matches when normalized current value ===
 *    normalized `control.default.value`). The backend trusts incoming targets
 *    are preset-cleared.
 * 3. Demote misconfigured controls (missing id, non-text, multi-path) via the
 *    shared `validateTranslationFlags` helper — same logic the authoring UI
 *    uses, so author warnings and runtime behaviour stay aligned.
 *
 * Backend dedupe (`normalizePhrase(value)`) collapses the per-placement
 * targets into the LLM-call sourceText set and builds `references[]` from
 * the same array, so this function intentionally returns one target per
 * placement slot rather than one per unique sourceText.
 */
export function extractTranslatableElements(
  params: ExtractParams,
): ExtractResult {
  const { videoSourceElements, extraElementData, targetLanguage } = params;
  const warnings: ExtractWarning[] = [];
  const compositionTargets: TranslatableTarget[] = [];
  const uniqueNormalizedSourceTexts = new Set<string>();
  let presetSkipped = 0;

  if (!Array.isArray(videoSourceElements) || videoSourceElements.length === 0) {
    return {
      compositionTargets: [],
      presetSkipped: 0,
      uniqueSourceTextCount: 0,
      warnings: [],
    };
  }

  for (const candidate of videoSourceElements) {
    if (!candidate || typeof candidate !== 'object') continue;
    // Karaoke captions live as top-level elements on the karaoke track and are
    // already translated through the transcript/word path — never as
    // composition phrases. Skip them so they don't get duplicated here.
    if ((candidate as { track?: unknown }).track === KARAOKE_TRACK_NUMBER)
      continue;
    const elementType = (candidate as { type?: unknown }).type;
    if (elementType !== 'composition') continue;
    const compositionElementId = (candidate as { id?: unknown }).id;
    if (typeof compositionElementId !== 'string' || compositionElementId === '')
      continue;

    const meta = readMeta(extraElementData, compositionElementId);
    if (!meta) {
      warnings.push({ kind: 'missing_meta', compositionElementId });
      continue;
    }
    const { templateId, controls } = meta;
    const { sanitized } = validateTranslationFlags(controls);
    // Warnings emitted by validateTranslationFlags are intentionally not
    // re-thrown here — the same authoring-time validator already surfaces
    // them in the DatoCMS UI. We do, however, defensively skip multi-path
    // controls below since the validator only warns without demoting.

    for (const control of sanitized) {
      if (!control.allowTranslation) continue;
      if (!Array.isArray(control.elementPaths) || control.elementPaths.length === 0)
        continue;
      if (control.elementPaths.length > 1) {
        // Validator already warned; defensively skip so we never produce
        // ambiguous (one-control → many-slots) targets in v1.
        warnings.push({
          kind: 'multi_path_translatable',
          compositionElementId,
          controlId: control.id,
          controlLabel: control.label,
          details: { pathCount: control.elementPaths.length },
        });
        continue;
      }

      const path = control.elementPaths[0];
      const leaf = traverseCompositionElementPath(candidate, path);
      if (!leaf) {
        warnings.push({
          kind: 'unresolved_path',
          compositionElementId,
          controlId: control.id,
          controlLabel: control.label,
          details: { path },
        });
        continue;
      }

      const value = (leaf as Record<string, unknown>)[control.property];
      if (typeof value !== 'string') {
        warnings.push({
          kind: 'non_string_value',
          compositionElementId,
          controlId: control.id,
          controlLabel: control.label,
          details: { property: control.property, valueType: typeof value },
        });
        continue;
      }
      if (value.trim() === '') {
        // Empty values aren't translatable; skip silently in production but
        // record for telemetry so we can spot misconfigured templates.
        warnings.push({
          kind: 'empty_value',
          compositionElementId,
          controlId: control.id,
          controlLabel: control.label,
        });
        continue;
      }

      if (isPresetCovered(control, value, targetLanguage)) {
        presetSkipped += 1;
        continue;
      }

      const sourceElementId =
        typeof (leaf as { id?: unknown }).id === 'string'
          ? ((leaf as { id: string }).id)
          : '';
      const sourceElementName =
        typeof (leaf as { name?: unknown }).name === 'string'
          ? ((leaf as { name: string }).name)
          : (control.label ?? '');
      const sourceElementType =
        typeof (leaf as { type?: unknown }).type === 'string'
          ? ((leaf as { type: string }).type)
          : 'text';

      if (sourceElementId === '') {
        // No stable id — can't track references reliably. Drop with a
        // warning rather than emitting a phantom target.
        warnings.push({
          kind: 'unresolved_path',
          compositionElementId,
          controlId: control.id,
          controlLabel: control.label,
          details: { reason: 'missing_source_element_id', path },
        });
        continue;
      }

      compositionTargets.push({
        compositionId: templateId,
        compositionElementId,
        sourceElement: {
          id: sourceElementId,
          name: sourceElementName,
          type: sourceElementType,
        },
        property: control.property,
        value,
      });
      uniqueNormalizedSourceTexts.add(normalizePhrase(value));
    }
  }

  // PLAT-10112 — standalone text overlays. A control-less top-level text
  // element has no authored translation flag yet (the `allowTranslation`
  // opt-out toggle is PLAT-9989), and the quick-edit popover already treats
  // such overlays as translatable by default. So emit a `kind: 'text'` target
  // for every top-level `type: 'text'` element with a non-empty `text`,
  // skipping only an explicit `allowTranslation === false`. The backend keys
  // text references on `textElementId` (Variant D, PLAT-9988).
  for (const candidate of videoSourceElements) {
    if (!candidate || typeof candidate !== 'object') continue;
    const element = candidate as Record<string, unknown>;
    // Skip karaoke caption text — handled by the transcript/word translation
    // path, not composition phrases (see the composition loop above).
    if (element.track === KARAOKE_TRACK_NUMBER) continue;
    if (element.type !== 'text') continue;
    if (element.allowTranslation === false) continue;
    const textElementId = element.id;
    if (typeof textElementId !== 'string' || textElementId === '') continue;
    const value = element.text;
    if (typeof value !== 'string' || value.trim() === '') continue;
    const name =
      typeof element.name === 'string' && element.name !== ''
        ? element.name
        : 'Text overlay';
    compositionTargets.push({
      kind: 'text',
      textElementId,
      sourceElement: { id: textElementId, name, type: 'text' },
      property: 'text',
      value,
    });
    uniqueNormalizedSourceTexts.add(normalizePhrase(value));
  }

  if (warnings.length > 0) {
    logger.warn('extractTranslatableElements produced warnings', {
      warningCount: warnings.length,
      // Surface the first few to keep the log line bounded.
      sample: warnings.slice(0, 5),
    });
  }

  return {
    compositionTargets,
    presetSkipped,
    uniqueSourceTextCount: uniqueNormalizedSourceTexts.size,
    warnings,
  };
}

interface CompositionMeta {
  templateId: string;
  controls: ControlConfiguration[];
}

function readMeta(
  extraElementData: Record<string, unknown> | null | undefined,
  compositionElementId: string,
): CompositionMeta | null {
  if (!extraElementData || typeof extraElementData !== 'object') return null;
  const bucket = (extraElementData as Record<string, unknown>)[compositionElementId];
  if (!bucket || typeof bucket !== 'object') return null;
  const templateId = (bucket as { templateId?: unknown }).templateId;
  const controls = (bucket as { compositionControls?: unknown }).compositionControls;
  if (typeof templateId !== 'string' || templateId === '') return null;
  if (!Array.isArray(controls)) return null;
  return {
    templateId,
    controls: controls as ControlConfiguration[],
  };
}

/**
 * Preset short-circuit (PLAT-9963 lock contract). A target is preset-covered
 * when:
 *
 *   - The control declares a translation for `targetLanguage`
 *     (`control.translations[targetLanguage]`)
 *   - AND the current original-language value matches the control's
 *     authored default (`control.default.value`) after normalization
 *
 * Preset-covered targets are never sent to the backend — the backend
 * rejects writes to preset-locked slots, and excluding them client-side
 * saves the round trip.
 *
 * **Language matching is by exact BCP-47 code.** A preset authored as
 * `translations: { 'es': 'Aviso' }` does NOT cover a `targetLanguage`
 * of `'es-MX'`. Authors must write the locale-specific code that matches
 * their showcase's supported languages — `'es-MX'` here, `'es-ES'` there.
 * Rationale: Arbor's showcase language config is BCP-47 keyed end-to-end
 * (transcript translations, language picker, persistence keys); falling
 * back from `'es-MX'` to `'es'` would silently broaden a producer's
 * intended scope. Authors who want one preset across every Spanish locale
 * should write the same string under each locale key in DatoCMS.
 */
function isPresetCovered(
  control: ControlConfiguration,
  currentValue: string,
  targetLanguage: string,
): boolean {
  const presetMap = control.translations;
  if (!presetMap || typeof presetMap !== 'object') return false;
  const presetForLang = presetMap[targetLanguage];
  if (typeof presetForLang !== 'string' || presetForLang === '') return false;
  const defaultValue = control.default?.value;
  if (typeof defaultValue !== 'string' || defaultValue === '') return false;
  return normalizePhrase(currentValue) === normalizePhrase(defaultValue);
}
